import { HttpClient } from "@angular/common/http";
import { Component, HostListener, Input, OnInit } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";
import { NgxLoggerLevel } from "ngx-logger";
import { MessageService } from "primeng/api";
import { DropdownChangeEvent } from "primeng/dropdown";
import { AppComponent } from "src/app/app.component";
import { ScrBaseComponent } from "src/app/components/base.component";
import { ClientInputWebsocketMessage, PendingOrderService, ScrAuthenticationService, ScrServiceManager, WebSocketService } from "src/app/services";
import { BillingOrder } from "../../models/billingorder.model";
import { REVISION } from "../../../environments/revision";

@Component({
  selector: 'app-utilities-dialog',
  templateUrl: './utilities-dialog.component.html',
  styleUrls: ['./utilities-dialog.component.scss']
})

export class UtilitiesDialogComponent extends ScrBaseComponent implements OnInit {

  public displayDialog = false;

  public tnOutput = '';
  public mbText = '';
  public logLevel = NgxLoggerLevel.DEBUG;
  public webappRevision!: string;
  public compiledInRevision = REVISION;
  public inlineMessageDebug = 'false';
  public inlineMessageDebugChecked = false;

  public idToken?: string;
  public refreshToken?: string;
  public accessToken?: string;
  public parsedIdToken?: string;
  public idTokenExpiration: Date | null = null;
  public signedIn = false;

  @Input() public app!: AppComponent;

  constructor(
    private auth: ScrAuthenticationService,
    private pendingOrderService: PendingOrderService,
    private httpClient: HttpClient,
    private wss: WebSocketService,
    private messageService: MessageService,
    public sm: ScrServiceManager) {
    super();
  }

  ngOnInit(): void {

    this.inlineMessageDebug = localStorage.getItem('scr.inlinemessage.debug') ?? 'false';
    this.inlineMessageDebugChecked = this.inlineMessageDebug === 'true';

    this.webappRevision = 'n/a';

    if (this.app) {
      this.logLevel = this.app.logLevel;
    }
  }

  onInlineMessageChange() {
    this.inlineMessageDebug = (this.inlineMessageDebugChecked) ? 'true' : 'false';
    console.debug('val is ' + this.inlineMessageDebug);
    localStorage.setItem('scr.inlinemessage.debug', this.inlineMessageDebug);
  }

  @HostListener('document:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.defaultPrevented) {
      return;
    }
    if ((event.code === 'KeyU') && (event.altKey)) {
      this.refreshMbContent();

      this.httpClient.get('assets/revision.txt', {
        responseType: 'text'
      }).subscribe({
        next: (rsp) => {
          this.webappRevision = rsp.trim();
        }
      });

      this.getMyToken();

      this.displayDialog = true;
      event.preventDefault();
    }

  }

  refreshMbContent(): void {
    const mb = this.pendingOrderService.getMarketingBrochureStorage();
    if (mb) {
      this.mbText = JSON.stringify(mb);
    }
    else {
      this.mbText = '';
    }
  }

  saveMbContent(mbtxt: HTMLTextAreaElement): void {
    try {
      console.debug('save ' + mbtxt.value);
      const mb = JSON.parse(mbtxt.value);
      this.pendingOrderService.storeMarketingBrochureContent(mb);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    catch (err: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: err.toString()
      });
    }
  }

  formatTn(tn: HTMLInputElement, country: HTMLInputElement): void {
    const utils = new PhoneNumberUtil();
    try {
      const parsed = utils.parse(tn.value, country.value);
      const LBLS = ['IS_POSSIBLE', 'INVALID_COUNTRY_CODE', 'TOO_SHORT', 'TOO_LONG', 'IS_POSSIBLE_LOCAL_ONLY', 'INVALID_LENGTH'];

      this.tnOutput = `
      is valid: ${utils.isValidNumber(parsed)}
      is possible number: ${utils.isPossibleNumber(parsed)}
      is possible reason: ${LBLS[utils.isPossibleNumberWithReason(parsed)]}
      phone number region: ${utils.getRegionCodeForNumber(parsed)}
      international: ${utils.format(parsed, PhoneNumberFormat.INTERNATIONAL)}
      national: ${utils.format(parsed, PhoneNumberFormat.NATIONAL)}
      e.164: ${utils.format(parsed, PhoneNumberFormat.E164)}
      rfc3996: ${utils.format(parsed, PhoneNumberFormat.RFC3966)}
    `;
    }
    catch (e) {
      this.tnOutput = `${e}`;
      console.error(e);
    }
  }

  sendMessage(msg: HTMLInputElement, delay: HTMLInputElement, size: HTMLInputElement): void {
    this.log.trace('send ' + msg.value + ' ' + delay.value + ' ' + size.value);
    if (this.wss.isOpen()) {
      const wsm = new ClientInputWebsocketMessage(msg.value, delay.value);
      if (size && size.value) {
        wsm.payload = ''.padEnd(parseInt(size.value, 10), 'x');
      }

      this.wss.send(wsm, true, 120_000, false).subscribe(rsp => {
        if (rsp.messages) {
          this.messageService.add({ severity: 'success', summary: rsp.messages[0].message });
        }
        else {
          this.messageService.add({ severity: 'success', summary: JSON.stringify(rsp) });
        }
      });

    }
    else {
      this.messageService.add({ severity: 'error', summary: 'Message could not be sent.  web socket connection is NOT open.' });
    }
  }

  restartWs(): void {
    this.wss.bounce().then(() => {
      this.log.trace("bounced");
    });
  }

  public setLevel(event: DropdownChangeEvent): void {
    console.debug('set log level to ' + JSON.stringify(event));
    this.app.logger.partialUpdateConfig({ level: event.value });
    localStorage.setItem('loglevel', `${event.value}`);

  }

  getMyToken(): void {
    this.auth.userIsSignedIn().then(b => {
      this.signedIn = b;
      if (b) {
        this.refreshToken = this.auth.getRefreshToken();
        this.auth.getIdToken().then(t => {
          this.idToken = t;
          const s = new JwtHelperService();
          if (t) {
            const decoded = s.decodeToken(t);
            this.idTokenExpiration = s.getTokenExpirationDate(t);
            this.parsedIdToken = JSON.stringify(decoded, undefined, 2);
          }
        });
      }
    });
  }

  refreshSession(): void {
    const astate = this.auth.getAuthState();
    if (astate?.username && astate?.refreshToken) {
      this.auth.exchangeRefreshToken(astate?.username).subscribe({
        next: (updated) => {
          this.log.trace('updated ' + updated);
          const astate = this.auth.getAuthState();
          this.log.trace('updated tokens ' + astate?.idToken + ' ' + astate?.refreshToken);
          this.idToken = astate?.idToken;
          if (astate?.idToken) {
            const s = new JwtHelperService();
            const decoded = s.decodeToken(astate.idToken);
            this.parsedIdToken = JSON.stringify(decoded, undefined, 2);
            this.refreshToken = astate?.refreshToken;
          }
        },
        error: (err) => {
          console.debug('error refreshing session ' + err);
        }
      });
    }
    this.sm.outstandingRequestCount.subscribe((count: number) => {
      this.app.setOutstanding(count);
    });
  }


  gtmLogin() {
    const as = this.auth.getAuthState();
    if (as?.userId && as?.accountId) {
      this.analyticsService.emitLogin(as.userId, as.accountId);
    }
  }

  gtmPayment() {
    const fakeOrder = new BillingOrder();
    fakeOrder.accountId = 123;
    fakeOrder.orderId = 345;
    fakeOrder.company = 'Google';
    fakeOrder.paymentMethod = 'Visa';
    this.analyticsService.emitPayment('false', fakeOrder);
  }
}
